import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Badge, buttonClass, inputClass } from '@/components/ui';
import { adminStockQuery } from '@/queries';
import type { Location, StockRow } from '@/lib/stock-types';
import type { RackPreset } from '@/pages/admin-locations';

/**
 * The rack map. One row of racking, drawn the way you see it from the aisle.
 *
 * Christian picked this from the design canvas on 23 September 2026, because
 * the plan is to show a photograph of what is in each tub. A list of 512 codes
 * cannot carry that; a picture of the rack with a panel beside it can.
 *
 * Everything here is worked out from `/api/locations`, which already returns
 * every location with its parts, its unit count, its lock and when it was last
 * counted. Only the side panel reads more, and only for the one shelf that is
 * selected.
 */

/** A shelf that holds stock and has not been counted for this long is flagged. */
const STALE_DAYS = 90;

type TubState = 'empty' | 'stock' | 'counting' | 'stale';

function stateOf(l: Location, now: number): TubState {
  if (l.locked_by_take_id) return 'counting';
  if (l.units <= 0) return 'empty';
  if (l.last_counted_at) {
    const age = (now - new Date(l.last_counted_at).getTime()) / 86_400_000;
    if (age > STALE_DAYS) return 'stale';
  }
  return 'stock';
}

const SWATCH: Record<TubState, string> = {
  empty: 'bg-panel ring-1 ring-inset ring-line-strong',
  stock: 'bg-ink',
  counting: 'bg-brand',
  stale: 'bg-danger-wash ring-1 ring-inset ring-[color:var(--color-danger)]',
};

const byNumber = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

interface Rack {
  key: string;
  site: string;
  siteName: string;
  row: string;
  locations: Location[];
}

/** Every location in a rack row, keyed by building and row. */
function racksOf(locations: Location[]): Rack[] {
  const map = new Map<string, Rack>();
  for (const l of locations) {
    if (!l.row_code) continue;
    const site = l.sites?.code ?? '';
    const key = `${site}|${l.row_code}`;
    const rack = map.get(key) ?? { key, site, siteName: l.sites?.name ?? '', row: l.row_code, locations: [] };
    rack.locations.push(l);
    map.set(key, rack);
  }
  return [...map.values()].sort((a, b) => byNumber(a.key, b.key));
}

/** A shelf is one bay at one height: every tub and compartment on it. */
function shelfKey(l: Location): string {
  return `${l.sites?.code ?? ''}|${l.row_code}|${l.bay ?? ''}|${l.shelf ?? ''}`;
}

function shelfCode(l: Location): string {
  return [l.sites?.code, l.row_code, l.bay, l.shelf].filter(Boolean).join('-');
}

function readFlip(): boolean {
  try {
    return localStorage.getItem('rack-map.shelf-a-top') === '1';
  } catch {
    return false;
  }
}

export function RackMap({
  locations, onAddRack,
}: {
  locations: Location[];
  /** Opens the racking form filled in. Spec 007 FR-703. */
  onAddRack?: (preset: Omit<RackPreset, 'key'>) => void;
}) {
  const racks = useMemo(() => racksOf(locations), [locations]);
  const [rackKey, setRackKey] = useState<string>(() => racks[0]?.key ?? '');
  const rack = racks.find((r) => r.key === rackKey) ?? racks[0];
  const [shelf, setShelf] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [find, setFind] = useState('');
  const [findSaid, setFindSaid] = useState<string | null>(null);
  // Which way up. Racking is labelled from the floor in most stores and from
  // the top in some, and only the people in the room know which this is.
  const [aTop, setATop] = useState<boolean>(readFlip);
  const now = Date.now();

  useEffect(() => {
    try {
      localStorage.setItem('rack-map.shelf-a-top', aTop ? '1' : '0');
    } catch {
      /* Private window. The choice lasts for this visit only. */
    }
  }, [aTop]);

  if (!rack) {
    return (
      <div className="rounded-[10px] border border-line bg-panel p-8 text-center text-[14px] text-muted">
        There is no racking yet. Use Add racking to set up a row, and it
        appears here as a map.
      </div>
    );
  }

  const bays = [...new Set(rack.locations.map((l) => l.bay ?? ''))].sort(byNumber);
  const shelvesUp = [...new Set(rack.locations.map((l) => l.shelf ?? ''))].sort(byNumber);
  const shelves = aTop ? shelvesUp : [...shelvesUp].reverse();

  const cells = new Map<string, Location[]>();
  for (const l of rack.locations) {
    const k = shelfKey(l);
    cells.set(k, [...(cells.get(k) ?? []), l]);
  }

  const held = rack.locations.filter((l) => l.units > 0).length;

  /**
   * Scan a label or type a code. A full code selects that compartment; part
   * of one selects the first shelf it matches, so `01-A-03` goes to bay 03.
   */
  function go() {
    const q = find.trim().toUpperCase();
    if (!q) return;
    const hit = locations.find((l) => l.code.toUpperCase() === q)
      ?? locations.find((l) => l.row_code && l.code.toUpperCase().startsWith(q));
    if (!hit || !hit.row_code) {
      setFindSaid(`No location starts with ${q}.`);
      return;
    }
    setFindSaid(null);
    setRackKey(`${hit.sites?.code ?? ''}|${hit.row_code}`);
    setShelf(shelfKey(hit));
    setPicked(hit.code.toUpperCase() === q ? hit.id : null);
  }

  const selected = shelf ? (cells.get(shelf) ?? []) : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line bg-panel p-3">
        <label className="flex min-w-[260px] flex-1 items-center gap-2">
          <span className="sr-only">Scan a label or type a code</span>
          <input
            type="search"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } }}
            placeholder="Scan a label or type a code, e.g. 01-A-03"
            className={`${inputClass} font-mono`}
          />
        </label>
        <button type="button" className={buttonClass('secondary', 'sm')} onClick={go}>
          Find
        </button>
        {/*
          * ALWAYS SHOWN, even for a single row. Tracker #4.
          *
          * Guy highlighted the left hand axis of the map, which reads SHELF A
          * to SHELF F, and asked whether it should read "Row A" instead. It
          * should not: that axis is the shelf height, and the row is named in
          * the heading beside it.
          *
          * But he had every reason to ask. His racking is Rows A to F AND
          * Shelves A to F, six of each, both lettered. The only row that
          * existed was A, so this picker was hidden by `racks.length > 1`, and
          * with it went the one control on the screen that says "a row is a
          * thing you choose". Six lettered bands down the side were then the
          * only lettering in sight.
          *
          * One button that says Row A is not clutter. It is the screen saying
          * what it is showing you.
          */}
        {racks.length > 0 && (
          <div role="group" aria-label="Row of racking" className="flex flex-wrap gap-1.5">
            {racks.map((r) => (
              <button
                key={r.key}
                type="button"
                aria-pressed={r.key === rack.key}
                onClick={() => { setRackKey(r.key); setShelf(null); setPicked(null); }}
                className={buttonClass(r.key === rack.key ? 'primary' : 'secondary', 'sm')}
              >
                {r.site ? `${r.site} · ` : ''}Row {r.row}
              </button>
            ))}
          </div>
        )}
        {findSaid && <p className="w-full text-[13px] text-muted">{findSaid}</p>}
      </div>

      <div className="flex flex-col gap-4 xl:flex-row">
        <section
          aria-label={`Row ${rack.row}`}
          className="min-w-0 flex-1 rounded-[10px] border border-line bg-panel p-4"
        >
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-[17px] font-semibold">
                {rack.site ? `Building ${rack.site}${rack.siteName ? ` ${rack.siteName}` : ''} · ` : ''}Row {rack.row}
              </h2>
              <span className="text-[13px] text-muted">
                {bays.length} bays · {rack.locations.length} locations · {held} hold stock
              </span>
            </div>
            <button
              type="button"
              className="text-[13px] text-muted underline"
              onClick={() => setATop((v) => !v)}
            >
              Shelf {shelvesUp[0]} is at the {aTop ? 'top' : 'bottom'}. Flip
            </button>
          </div>

          <Legend />

          <div className="mt-3 overflow-x-auto">
            <div
              className="grid min-w-max gap-2"
              style={{ gridTemplateColumns: `56px repeat(${bays.length}, minmax(112px, 1fr))` }}
            >
              <div />
              {bays.map((b) => (
                <div key={b} className="pb-0.5 text-center font-mono text-[12px] text-muted">
                  BAY {b}
                </div>
              ))}
              {shelves.map((s) => (
                <ShelfRow
                  key={s}
                  shelf={s}
                  bays={bays}
                  rack={rack}
                  cells={cells}
                  now={now}
                  selected={shelf}
                  onPick={(k) => { setShelf(k); setPicked(null); }}
                  onAddRack={onAddRack}
                />
              ))}
            </div>
          </div>
        </section>

        <aside
          aria-label="Selected shelf"
          className="w-full shrink-0 rounded-[10px] border border-line bg-panel p-4 xl:w-[380px]"
        >
          {selected.length === 0 ? (
            <p className="text-[14px] text-muted">
              Select a shelf on the map to see what is in each tub, count it, or
              print its labels.
            </p>
          ) : (
            <ShelfPanel
              key={shelf}
              locations={selected}
              bayShelves={[...new Set(
                rack.locations
                  .filter((l) => l.bay === selected[0]?.bay)
                  .map((l) => l.shelf ?? ''),
              )].filter(Boolean)}
              picked={picked}
              onPick={setPicked}
              now={now}
              onAddRack={onAddRack}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

function Legend() {
  const items: Array<[TubState, string]> = [
    ['empty', 'Empty'],
    ['stock', 'Holds stock'],
    ['counting', 'Being counted'],
    ['stale', `Not counted in ${STALE_DAYS} days`],
  ];
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2">
      {items.map(([k, label]) => (
        <li key={k} className="flex items-center gap-1.5">
          <span className={`h-3 w-3 rounded-[3px] ${SWATCH[k]}`} aria-hidden="true" />
          {label}
        </li>
      ))}
    </ul>
  );
}

/**
 * WHAT GOES IN A GAP. Christian, 6 October 2026.
 *
 * A gap is a shelf that another bay in this row has and this bay does not:
 * shelf F of row F, bay 02, removed before Remove stopped deleting. Putting it
 * back used to mean clicking the shelf BELOW and choosing "Add a shelf", which
 * nobody would guess. The gap now carries its own button.
 *
 * The tubs and compartments are copied from the same shelf in the nearest bay
 * that has it, because cabinets in one row are usually the same; failing that,
 * from the nearest shelf below in this bay. The racking form then adds what is
 * missing and brings back what was removed (spec 007 D-2), so a tub that was
 * only hidden comes back with its old code and history, not as a copy.
 */
export function gapPreset(
  rack: { site: string; row: string; locations: Location[] },
  bay: string,
  shelf: string,
): Omit<RackPreset, 'key'> | null {
  const siteId = rack.locations.find((l) => l.site_id)?.site_id;
  if (!siteId) return null;

  const at = (b: string, sh: string) =>
    rack.locations.filter((l) => l.bay === b && l.shelf === sh);
  const bays = [...new Set(rack.locations.map((l) => l.bay ?? ''))].filter(Boolean).sort(byNumber);
  const here = bays.indexOf(bay);
  const byDistance = bays
    .filter((b) => b !== bay)
    .sort((a, b) => Math.abs(bays.indexOf(a) - here) - Math.abs(bays.indexOf(b) - here));

  let template: Location[] = [];
  for (const b of byDistance) {
    template = at(b, shelf);
    if (template.length) break;
  }
  if (!template.length) {
    const below = [...new Set(rack.locations.filter((l) => l.bay === bay).map((l) => l.shelf ?? ''))]
      .filter((x) => x && x < shelf)
      .sort(byNumber)
      .reverse();
    for (const sh of below) {
      template = at(bay, sh);
      if (template.length) break;
    }
  }

  const tubs = [...new Set(template.map((l) => l.tub ?? ''))].filter(Boolean).sort(byNumber);
  const subs = [...new Set(template.map((l) => l.sub ?? ''))].filter(Boolean).sort(byNumber);
  return {
    siteId,
    row: rack.row,
    bayFrom: String(Number(bay)), bayTo: String(Number(bay)),
    shelfFrom: shelf, shelfTo: shelf,
    tubFrom: tubs.length ? String(Number(tubs[0])) : '',
    tubTo: tubs.length ? String(Number(tubs[tubs.length - 1])) : '',
    subFrom: tubs.length ? (subs[0] ?? '') : '',
    subTo: tubs.length ? (subs[subs.length - 1] ?? '') : '',
    title: `Add shelf ${shelf} to bay ${bay}, row ${rack.row}`,
  };
}

function ShelfRow({
  shelf, bays, rack, cells, now, selected, onPick, onAddRack,
}: {
  shelf: string;
  bays: string[];
  rack: Rack;
  cells: Map<string, Location[]>;
  now: number;
  selected: string | null;
  onPick: (key: string) => void;
  onAddRack?: (preset: Omit<RackPreset, 'key'>) => void;
}) {
  return (
    <>
      <div className="flex items-center font-mono text-[12px] text-muted">SHELF {shelf}</div>
      {bays.map((bay) => {
        const key = `${rack.site}|${rack.row}|${bay}|${shelf}`;
        const locs = cells.get(key) ?? [];
        if (locs.length === 0) {
          const preset = onAddRack ? gapPreset(rack, bay, shelf) : null;
          if (!preset) return <div key={bay} />;
          // Quiet on purpose: a gap may be a shorter cabinet, not a mistake.
          return (
            <button
              key={bay}
              type="button"
              onClick={() => onAddRack!(preset)}
              aria-label={`Add shelf ${shelf} to bay ${bay}, row ${rack.row}`}
              className="flex min-h-[96px] flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-line-strong text-[12px] text-muted transition-colors hover:border-ink hover:bg-bg hover:text-ink"
            >
              <span aria-hidden="true" className="text-[20px] leading-none">+</span>
              <span>Add shelf {shelf}</span>
            </button>
          );
        }
        return (
          <ShelfCell
            key={bay}
            locations={locs}
            now={now}
            selected={selected === key}
            onPick={() => onPick(key)}
          />
        );
      })}
    </>
  );
}

/**
 * One shelf in one bay. Tubs run across, compartments run down, so the little
 * grid inside the cell is laid out the way the tubs sit on the shelf.
 */
function ShelfCell({
  locations, now, selected, onPick,
}: {
  locations: Location[];
  now: number;
  selected: boolean;
  onPick: () => void;
}) {
  const tubs = [...new Set(locations.map((l) => l.tub ?? ''))].sort(byNumber);
  const subs = [...new Set(locations.map((l) => l.sub ?? ''))].sort(byNumber);
  const at = new Map(locations.map((l) => [`${l.tub ?? ''}|${l.sub ?? ''}`, l]));
  const full = locations.filter((l) => l.units > 0).length;
  const counting = locations.some((l) => l.locked_by_take_id);
  const code = shelfCode(locations[0]!);

  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      aria-label={`${code}, ${full} of ${locations.length} hold stock${counting ? ', being counted' : ''}`}
      className={
        'flex min-h-[96px] flex-col justify-between gap-2 rounded-[8px] p-2.5 text-left transition-colors '
        + (selected
          ? 'border-2 border-ink bg-brand-wash-2'
          : 'border border-line bg-bg hover:border-line-strong')
      }
    >
      <span className="font-mono text-[11px] text-muted">{code}</span>
      <span
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${tubs.length}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {subs.map((s) => tubs.map((t) => {
          const l = at.get(`${t}|${s}`);
          return (
            <span
              key={`${t}|${s}`}
              className={`h-3 rounded-[3px] ${l ? SWATCH[stateOf(l, now)] : ''}`}
            />
          );
        }))}
      </span>
      <span className="text-[12px] text-ink-2">
        {counting ? 'Being counted' : full === 0 ? 'Empty' : `${full} of ${locations.length} hold stock`}
      </span>
    </button>
  );
}

/** The first photograph of the product, when there is one. */
function thumbOf(row: StockRow): { url: string; alt: string } | null {
  const imgs = row.items?.product_variants?.products?.product_images ?? [];
  const first = [...imgs].sort((a, b) => a.sort - b.sort)[0];
  return first ? { url: first.url, alt: first.alt } : null;
}

function Thumb({ row }: { row: StockRow }) {
  const img = thumbOf(row);
  if (img) {
    return (
      <img
        src={img.url}
        alt={img.alt}
        loading="lazy"
        className="h-11 w-11 shrink-0 rounded-[6px] border border-line bg-bg object-cover"
      />
    );
  }
  // No photograph yet. A plain tile with the colour and size, so the row still
  // reads at a glance and the layout does not move when photographs arrive.
  const v = row.items?.product_variants;
  return (
    <span
      className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-[6px] border border-line bg-raised text-center font-mono text-[10px] leading-tight text-muted"
      aria-hidden="true"
    >
      {v ? (
        <>
          <span className="max-w-[40px] truncate">{v.colour?.slice(0, 5) ?? ''}</span>
          <span className="font-semibold text-ink-2">{v.size}</span>
        </>
      ) : (
        <span>{row.items?.base_uom}</span>
      )}
    </span>
  );
}

function ShelfPanel({
  locations, bayShelves, picked, onPick, now, onAddRack,
}: {
  locations: Location[];
  /** Every shelf letter this bay has, so "add a shelf" knows the next one. */
  bayShelves: string[];
  picked: string | null;
  onPick: (id: string | null) => void;
  now: number;
  onAddRack?: (preset: Omit<RackPreset, 'key'>) => void;
}) {
  const qc = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);
  const sorted = [...locations].sort((a, b) => byNumber(a.code, b.code));
  const ids = sorted.map((l) => l.id);
  const first = sorted[0]!;
  const tubs = [...new Set(sorted.map((l) => l.tub ?? ''))].sort(byNumber);

  // One read for the whole shelf, not one per compartment.
  const stock = useQuery({
    ...adminStockQuery({ location: ids.join(','), limit: 200 }),
    enabled: sorted.some((l) => l.units > 0),
  });
  const rows = stock.data?.rows ?? [];

  const open = useMutation({
    mutationFn: async (locationId: string) => {
      const res = await fetch('/api/stock-takes', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ location_id: locationId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
    },
    onSuccess: () => {
      setSaid('The count is open. Go to Stocktakes to enter it.');
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'stock-takes'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const pickedLoc = sorted.find((l) => l.id === picked) ?? null;
  const shown = pickedLoc ? [pickedLoc] : sorted;
  const units = sorted.reduce((a, l) => a + l.units, 0);
  const held = sorted.filter((l) => l.units > 0).length;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="font-mono text-[11px] tracking-[0.08em] text-faint">SELECTED SHELF</div>
        <div className="mt-1 font-mono text-[24px] font-semibold">{shelfCode(first)}</div>
        <div className="mt-0.5 text-[13px] text-muted">
          Row {first.row_code} · Bay {first.bay} · Shelf {first.shelf} · {tubs.length}{' '}
          {tubs.length === 1 ? 'tub' : 'tubs'} · {held} of {sorted.length} hold stock
          {units > 0 ? ` · ${units} units` : ''}
        </div>
      </div>

      {onAddRack && first.site_id && first.row_code && first.bay && (() => {
        /*
         * ADD A SHELF OR A TUB WITHOUT FILLING IN RANGES. Spec 007 FR-703.
         *
         * Guy, 5 October 2026: if a shelf is removed by accident he wants to put
         * it back himself. The racking form adds only what is missing and brings
         * back what was removed, so these two buttons fill it in and leave the
         * last word to the person.
         */
        const subs = [...new Set(sorted.map((l) => l.sub ?? ''))].filter(Boolean).sort(byNumber);
        const tubNums = tubs.filter(Boolean);
        const lastTub = tubNums.length ? Number(tubNums[tubNums.length - 1]) : 0;
        const shelvesSorted = [...bayShelves].sort(byNumber);
        const top = shelvesSorted[shelvesSorted.length - 1] ?? 'A';
        const nextShelf = String.fromCharCode(Math.min(90, top.charCodeAt(0) + 1));
        const base = {
          siteId: first.site_id!, row: first.row_code!,
          bayFrom: String(Number(first.bay)), bayTo: String(Number(first.bay)),
          subFrom: subs[0] ?? '', subTo: subs[subs.length - 1] ?? '',
        };
        return (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className={buttonClass('secondary', 'sm', 'justify-center')}
              onClick={() => onAddRack({
                ...base,
                shelfFrom: first.shelf ?? 'A', shelfTo: first.shelf ?? 'A',
                tubFrom: String(lastTub + 1), tubTo: String(lastTub + 1),
                title: `Add a tub to shelf ${first.shelf}, bay ${first.bay}, row ${first.row_code}`,
              })}
            >
              Add a tub
            </button>
            <button
              type="button"
              className={buttonClass('secondary', 'sm', 'justify-center')}
              onClick={() => onAddRack({
                ...base,
                shelfFrom: nextShelf, shelfTo: nextShelf,
                tubFrom: tubNums.length ? String(Number(tubNums[0])) : '',
                tubTo: tubNums.length ? String(lastTub) : '',
                title: `Add a shelf to bay ${first.bay}, row ${first.row_code}`,
              })}
            >
              Add a shelf
            </button>
          </div>
        );
      })()}

      <a
        href={`/api/locations/labels?ids=${ids.join(',')}`}
        target="_blank"
        rel="noreferrer"
        className={buttonClass('secondary', 'sm', 'w-full justify-center')}
      >
        Print {sorted.length} {sorted.length === 1 ? 'label' : 'labels'} for this shelf
      </a>

      {tubs.map((t) => {
        const inTub = sorted.filter((l) => (l.tub ?? '') === t);
        return (
          <div key={t} className="flex flex-col gap-1.5">
            <div className="text-[12px] font-semibold text-ink-2">{t ? `Tub ${t}` : 'On the shelf'}</div>
            <div className="flex flex-wrap gap-1.5">
              {inTub.map((l) => {
                const st = stateOf(l, now);
                const on = picked === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onPick(on ? null : l.id)}
                    title={l.code}
                    className={
                      'flex h-11 min-w-[64px] items-center gap-2 rounded-[6px] border px-2.5 text-[13px] '
                      + (on ? 'border-ink bg-brand-wash-2 font-semibold' : 'border-line bg-bg hover:border-line-strong')
                    }
                  >
                    <span className={`h-2.5 w-2.5 rounded-[2px] ${SWATCH[st]}`} aria-hidden="true" />
                    <span className="font-mono">{l.sub ?? l.tub ?? l.code}</span>
                    <span className="text-muted">{l.units > 0 ? l.units : ''}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {pickedLoc && (
        <div className="flex flex-col gap-2 rounded-[8px] border border-line bg-bg p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[15px] font-semibold">{pickedLoc.code}</span>
            {pickedLoc.locked_by_take_id && <Badge tone="warn">being counted</Badge>}
          </div>
          <div className="text-[12px] text-muted">
            {pickedLoc.last_counted_at
              ? `Last counted ${new Date(pickedLoc.last_counted_at).toLocaleDateString('en-AU')}`
              : 'Never counted'}
          </div>
          <div className="flex gap-2">
            {pickedLoc.locked_by_take_id ? (
              <Link to="/admin/stock-takes" className={buttonClass('primary', 'sm', 'flex-1 justify-center')}>
                Go to the count
              </Link>
            ) : (
              <button
                type="button"
                disabled={open.isPending}
                onClick={() => { setSaid(null); open.mutate(pickedLoc.id); }}
                className={buttonClass('primary', 'sm', 'flex-1 justify-center')}
              >
                {open.isPending ? 'Opening' : 'Count this compartment'}
              </button>
            )}
            <a
              href={`/api/locations/${pickedLoc.id}/label`}
              target="_blank"
              rel="noreferrer"
              className={buttonClass('secondary', 'sm')}
            >
              Print label
            </a>
          </div>
          {said && <p className="text-[13px] text-muted">{said}</p>}
        </div>
      )}

      <div className="border-t border-line pt-3">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-[14px] font-semibold">
            {pickedLoc ? 'In this compartment' : 'On this shelf'}
          </h3>
          {pickedLoc && (
            <button type="button" className="text-[12px] text-muted underline" onClick={() => onPick(null)}>
              Show the whole shelf
            </button>
          )}
        </div>
        <Contents
          locations={shown}
          rows={rows}
          loading={stock.isPending && stock.fetchStatus !== 'idle'}
          failed={stock.isError ? (stock.error as Error).message : null}
        />
      </div>
    </div>
  );
}

function Contents({
  locations, rows, loading, failed,
}: {
  locations: Location[];
  rows: StockRow[];
  loading: boolean;
  failed: string | null;
}) {
  if (failed) return <p className="text-[13px] text-muted">{failed}</p>;
  if (loading) return <p className="text-[13px] text-muted">Loading what is on the shelf…</p>;

  const ids = new Set(locations.map((l) => l.id));
  // A line with no location is on no shelf, so it is on no shelf in this rack
  // either. The rack map never asks for that list, and this says so. #13.
  const mine = rows.filter(
    (r) => r.location_id != null && ids.has(r.location_id) && Number(r.on_hand) !== 0,
  );
  if (mine.length === 0) {
    return <p className="text-[13px] text-muted">Nothing is recorded here.</p>;
  }

  const codeOf = new Map(locations.map((l) => [l.id, l.code]));
  return (
    <ul className="flex flex-col gap-1.5">
      {mine.map((r) => (
        <li key={`${r.location_id}-${r.item_id}`} className="flex items-center gap-2.5 rounded-[6px] bg-bg p-1.5 pr-2.5">
          <Thumb row={r} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px]">{r.items?.name}</span>
            <span className="block truncate font-mono text-[11px] text-muted">
              {r.items?.code} · {codeOf.get(r.location_id ?? '')}
            </span>
          </span>
          <span className="font-mono text-[14px] font-semibold">{Number(r.on_hand)}</span>
        </li>
      ))}
    </ul>
  );
}
